// Utilidad compartida para el sistema de filtrado del catálogo público.
// Centraliza el contrato de query params descrito en docs/CATALOGO_FILTROS_FRONTEND.md
// para que Venta, Alquiler, Luxury y Desarrollos usen exactamente los mismos filtros.

// Grupos de filtros booleanos que el backend espera como params individuales `clave=true`.
// En la URL del frontend se guardan como lista separada por comas para acortar la dirección.
export const BOOLEAN_FILTER_GROUPS = [
  'characteristics',
  'amenities',
  'services',
  'environmentsOptions',
];

// Claves de filtros escalares (string/number) que se sincronizan con la URL tal cual.
export const SCALAR_FILTER_KEYS = [
  // Ubicación
  'province',
  'country',
  'locality',
  'neighborhood',
  'departments',
  // Precio y moneda
  'minPrice',
  'maxPrice',
  'currency',
  // Tipo y estado
  'propertyType',
  'propertyState',
  // Ambientes (conteos)
  'environments',
  'rooms',
  'bathrooms',
  'toilettes',
  'garages',
  // Medidas (rangos en m²)
  'minTotalSquareMeters',
  'maxTotalSquareMeters',
  'minCoveredSquareMeters',
  'maxCoveredSquareMeters',
  'minSemiCoveredSquareMeters',
  'maxSemiCoveredSquareMeters',
  'minUncovered',
  'maxUncovered',
  'minLand',
  'maxLand',
  // Antigüedad
  'minAge',
  'maxAge',
  // Ordenamiento
  'orderBy',
];

// Estado inicial de filtros: escalares vacíos + grupos booleanos como arrays.
export const DEFAULT_FILTERS = {
  ...SCALAR_FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {}),
  ...BOOLEAN_FILTER_GROUPS.reduce((acc, key) => ({ ...acc, [key]: [] }), {}),
};

// Normaliza el valor de un grupo booleano a array (acepta array o string "a,b").
const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim() !== '') {
    return value.split(',').map((v) => v.trim()).filter(Boolean);
  }
  return [];
};

// Lee los filtros desde los searchParams de la URL.
export const getFiltersFromSearchParams = (searchParams) => {
  const filters = { ...DEFAULT_FILTERS };

  SCALAR_FILTER_KEYS.forEach((key) => {
    filters[key] = searchParams.get(key) || '';
  });

  BOOLEAN_FILTER_GROUPS.forEach((key) => {
    filters[key] = toArray(searchParams.get(key));
  });

  return filters;
};

// Convierte un objeto de filtros a un objeto plano apto para setSearchParams
// (omite valores vacíos y serializa los grupos booleanos como lista separada por comas).
export const filtersToSearchParams = (filters = {}, extra = {}) => {
  const params = {};

  SCALAR_FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      params[key] = value;
    }
  });

  BOOLEAN_FILTER_GROUPS.forEach((key) => {
    const arr = toArray(filters[key]);
    if (arr.length > 0) {
      params[key] = arr.join(',');
    }
  });

  return { ...params, ...extra };
};

// Construye el query string para la API expandiendo los grupos booleanos
// a params individuales `clave=true`, tal como lo espera el backend.
export const buildPropertyQuery = (filters = {}, page = 1, options = {}) => {
  const params = new URLSearchParams();

  SCALAR_FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      params.append(key, `${value}`.trim());
    }
  });

  BOOLEAN_FILTER_GROUPS.forEach((key) => {
    toArray(filters[key]).forEach((flag) => {
      params.append(flag, 'true');
    });
  });

  if (page) params.append('page', page);
  if (options.limit) params.append('limit', options.limit);

  return params;
};

// Devuelve el endpoint completo con el query string aplicado.
export const buildPropertyEndpoint = (baseEndpoint, filters = {}, page = 1, options = {}) => {
  const params = buildPropertyQuery(filters, page, options);
  const queryString = params.toString();
  return queryString ? `${baseEndpoint}?${queryString}` : baseEndpoint;
};

// Indica si hay algún filtro activo (excluyendo paginación).
export const hasActiveFilters = (filters = {}) => {
  const scalarActive = SCALAR_FILTER_KEYS.some(
    (key) => `${filters[key] ?? ''}`.trim() !== ''
  );
  const booleanActive = BOOLEAN_FILTER_GROUPS.some(
    (key) => toArray(filters[key]).length > 0
  );
  return scalarActive || booleanActive;
};
